import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';
import { LandingNav } from './LandingNav';
import Footer from '../Layout/Footer';
import { ArrowRight, Check } from 'lucide-react';
import { ComparisonSlider } from '../UI/ComparisonSlider';
import { usePendingSubscription } from '../../hooks/usePendingSubscription';
import { CitationList } from '../Marketing/CitationList';
import { LandingTransformationProof } from './LandingTransformationProof';
import { LandingListingProof } from './LandingListingProof';
import { LandingStyleBreadthProof } from './LandingStyleBreadthProof';
import { LandingWorkflowProof } from './LandingWorkflowProof';
import {
  buildCanonical,
  buildFaqJsonLd,
  homepageFaqItems,
  siteMetadata,
} from '../../content/marketing/faqContent';
import {
  transformationProofContent,
  listingProofContent,
  styleBreadthContent,
  workflowProofContent,
} from './landingProofContent';

// ============================================================================
// Comparison Examples (module-scoped — no re-allocation per render)
// ============================================================================

const COMPARISON_EXAMPLES = [
  {
    label: 'Living Room',
    style: 'Modern',
    before: '/before-and-after/interior/living-spaces/living-room-modern-before.jpeg',
    after: '/before-and-after/interior/living-spaces/living-room-modern-after.jpeg',
  },
  {
    label: 'Kitchen',
    style: 'Modern',
    before: '/before-and-after/interior/kitchen/kitchen-island-showcase-before.jpeg',
    after: '/before-and-after/interior/kitchen/kitchen-island-showcase-after.jpeg',
  },
  {
    label: 'Bedroom',
    style: 'Luxury',
    before: '/before-and-after/interior/bedrooms/primary-bedroom-luxury-showcase-before.jpeg',
    after: '/before-and-after/interior/bedrooms/primary-bedroom-luxury-showcase-after.jpeg',
  },
  {
    label: 'Dining Room',
    style: 'Traditional',
    before: '/before-and-after/interior/dining/dining-room-traditional-before.jpeg',
    after: '/before-and-after/interior/dining/dining-room-traditional-after.jpeg',
  },
  {
    label: 'Backyard',
    style: 'Coastal',
    before: '/before-and-after/exterior/outdoor-spaces/backyard-coastal-before.jpeg',
    after: '/before-and-after/exterior/outdoor-spaces/backyard-coastal-showcase-after.jpeg',
  },
  {
    label: 'Patio',
    style: 'Coastal',
    before: '/before-and-after/exterior/outdoor-living/patio-coastal-before.jpeg',
    after: '/before-and-after/exterior/outdoor-living/patio-coastal-showcase-after.jpeg',
  },
];

const HERO_IMAGES = [
  '/images/styles-page/coastal_staging_style.webp',
  '/images/styles-page/modern_staging_style.webp',
  '/images/styles-page/luxury_staging_style.webp',
  '/images/styles-page/scandinavian_staging_style.webp',
];

const STYLE_SHOWCASE = [
  { style: 'coastal', image: '/images/styles-page/coastal_staging_style.webp' },
  { style: 'modern', image: '/images/styles-page/modern_staging_style.webp' },
  { style: 'luxury', image: '/images/styles-page/luxury_staging_style.webp' },
  { style: 'contemporary', image: '/images/styles-page/contemporary_staging_style.webp' },
  { style: 'scandinavian', image: '/images/styles-page/scandinavian_staging_style.webp' },
];

const LISTING_OUTCOMES = [
  {
    eyebrow: 'Before and after proof',
    title: 'Show sellers exactly what changes before the listing goes live.',
    description:
      'Use same-room comparisons to explain the marketing lift clearly instead of asking clients to imagine the result from a static staged image alone.',
    image: '/images/styles-page/coastal_staging_style.webp',
    bullets: ['Dedicated before-and-after library', 'Interior and exterior proof', 'Seller presentation ready'],
    to: '/virtual-staging/before-after',
    cta: 'See virtual staging before and after',
  },
  {
    eyebrow: 'MLS-ready media',
    title: 'Keep the architecture real and the export workflow practical.',
    description:
      'The goal is not fantasy art. The goal is a believable marketing image that can move from draft to MLS, email, and social without a second production loop.',
    image: '/images/styles-page/empty_room_proof_modern.webp',
    bullets: ['HD and 4K downloads', 'Disclosure-aware FAQ', 'Built for listing media'],
    to: '/virtual-staging/faq',
    cta: 'Read the MLS and disclosure FAQ',
  },
  {
    eyebrow: 'Buyer-fit styling',
    title: 'Restage the same room for a different buyer profile without a reshoot.',
    description:
      'Use style variation to support pricing conversations, seller presentations, and audience-specific marketing without touching the camera angle.',
    image: '/images/styles-page/empty_room_proof_scandinavian.webp',
    bullets: ['21 design styles', 'Same-room controlled variation', 'Works across listing types'],
    to: '/virtual-staging/styles',
    cta: 'Browse all 21 styles',
  },
];

const HOME_WORKFLOW_STEPS = [
  {
    number: '01',
    title: 'Upload the listing photo',
    description:
      'Start with an empty room image that is level, bright, and easy to read. iPhone-quality photos can work well when the geometry is clear.',
    image: '/images/styles-page/no_furniture_empty_room.webp',
    tags: ['Empty room', 'Clean framing', 'Fast upload'],
  },
  {
    number: '02',
    title: 'Choose the style that fits the likely buyer',
    description:
      'Select from 21 staging styles, then match the room to the buyer story you want the listing to tell instead of relying on a one-look-fits-all presentation.',
    image: '/images/styles-page/empty_room_proof_coastal.webp',
    tags: ['21 styles', 'Buyer-fit direction', 'Restage instantly'],
  },
  {
    number: '03',
    title: 'Download the staged file and publish',
    description:
      'Move from draft to delivery in about 30 seconds with download options built for MLS galleries, listing presentations, and social distribution.',
    image: '/images/styles-page/empty_room_proof_modern.webp',
    tags: ['HD', '4K', 'MLS-ready'],
  },
];

const AGENT_WORKFLOWS = [
  {
    role: 'Solo listing agent',
    market: 'Raleigh, NC',
    listing: 'Vacant townhouse relaunch',
    benefit:
      'Stages the primary living area in two directions before the seller meeting, then uses the stronger hero image for MLS and the alternate look for follow-up marketing.',
  },
  {
    role: 'Photographer partner',
    market: 'Scottsdale, AZ',
    listing: 'Spec-home media package',
    benefit:
      'Adds staged variants after the shoot so the client can market the property without waiting for furniture install, pickup, or a second camera day.',
  },
  {
    role: 'Brokerage marketing lead',
    market: 'Dallas, TX',
    listing: 'Multi-listing monthly pipeline',
    benefit:
      'Keeps image style, export quality, and handoff consistent across agents so every vacant listing launches with the same standard of visual proof.',
  },
];

const LANDING_PRICING_PLANS = [
  {
    name: 'Starter',
    description: 'For agents testing a lighter listing volume',
    monthlyPrice: 19,
    annualPrice: 15,
    monthlyImages: 60,
    annualImages: 60,
    features: ['60 staged photos per month', '21 design styles', 'HD downloads', 'Email support'],
    cta: 'Start Free Trial',
    highlight: false,
  },
  {
    name: 'Professional',
    description: 'For active agents who need more volume and 4K exports',
    monthlyPrice: 39,
    annualPrice: 29,
    monthlyImages: 180,
    annualImages: 180,
    features: [
      '180 staged photos per month',
      '21 design styles',
      '4K ultra HD downloads',
      'Priority support',
      'Bulk processing',
      'Team collaboration',
    ],
    cta: 'Start Free Trial',
    highlight: true,
  },
  {
    name: 'Premium',
    description: 'For brokerages and teams that need staging at scale',
    monthlyPrice: 79,
    annualPrice: 59,
    monthlyImages: 400,
    annualImages: 400,
    features: [
      '400 staged photos per month',
      'All design styles',
      '4K ultra HD downloads',
      'Dedicated success manager',
      'API access',
      'White-label option',
    ],
    cta: 'Contact Sales',
    highlight: false,
  },
];

// ============================================================================
// Component
// ============================================================================

interface LandingPageProps {
  onGetStarted: () => void;
}

export function LandingPage({ onGetStarted }: LandingPageProps) {
  // Check for pending subscription when landing page loads
  // This handles the case where user clicks email confirmation link
  usePendingSubscription();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');
  const [scrollY, setScrollY] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);
  const [activeComparison, setActiveComparison] = useState(0);
  const [isSliderTransitioning, setIsSliderTransitioning] = useState(false);

  const handleExampleChange = (index: number) => {
    if (index === activeComparison || isSliderTransitioning) return;
    setIsSliderTransitioning(true);
    setTimeout(() => {
      setActiveComparison(index);
      setIsSliderTransitioning(false);
    }, 150);
  };

  // Prefetch all comparison images so switches are instant
  useEffect(() => {
    COMPARISON_EXAMPLES.forEach((ex) => {
      const b = new Image();
      b.src = ex.before;
      const a = new Image();
      a.src = ex.after;
    });
  }, []);

  // Preload first hero for LCP
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = '/images/styles-page/coastal_staging_style.webp';
    document.head.appendChild(link);
    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const homepageJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'AI Virtual Staging for Real Estate Agents',
      description:
        'AI virtual staging for real estate. Create MLS-ready listing photos in about 30 seconds, compare before-and-after results, and choose from 21 styles.',
      url: buildCanonical('/'),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteMetadata.name,
      url: siteMetadata.url,
      logo: siteMetadata.logo,
      sameAs: siteMetadata.socialProfiles,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'VirtualStagePro',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: siteMetadata.url,
      description:
        'AI virtual staging software for real estate agents with before-and-after proof, MLS-ready downloads, and 21 designer styles.',
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '19.00',
        highPrice: '79.00',
        priceCurrency: 'USD',
        offerCount: '3',
      },
    },
    buildFaqJsonLd(homepageFaqItems),
  ];

  return (
    <div className="bg-alabaster text-charcoal min-h-screen">
      <SEO
        title="AI Virtual Staging for Real Estate Agents | VirtualStagePro"
        description="AI virtual staging for real estate. Create MLS-ready listing photos in about 30 seconds, compare before-and-after results, and choose from 21 styles."
        openGraph={{ image: '/images/styles-page/coastal_staging_style.webp' }}
        canonical={buildCanonical('/')}
        jsonLd={homepageJsonLd}
      />

      {/* Floating Navigation */}
      <LandingNav onGetStarted={onGetStarted} />

      {/* Hero Section - Full Screen with Warm Overlay */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {HERO_IMAGES.map((src, index) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: prefersReducedMotion 
                    ? 'none' 
                    : `scale(${1 + (scrollY / (typeof window !== 'undefined' ? window.innerHeight : 800)) * 0.1})`,
                }}
              >
                <img
                  src={src}
                  alt="Virtual staging example"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Warm gradient overlay - Atelier style */}
              <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/30 to-olive/40" />
            </div>
          ))}
        </div>

        {/* Soft terracotta glow accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120vw] h-[30vh] bg-gradient-to-t from-terracotta/10 to-transparent blur-3xl pointer-events-none" />
        
        <div className="relative z-10 text-center max-w-5xl mx-auto px-6 pt-20 md:pt-0">
          <div className="mb-8 inline-flex items-center px-4 py-2 rounded-full bg-alabaster/20 backdrop-blur-sm border border-alabaster/30">
            <span className="text-xs font-mono uppercase tracking-widest text-alabaster/90">
              Virtual Staging, Reimagined
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-serif font-light tracking-tight mb-8 leading-none text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
            Stage Any Property
            <br />
            <span className="text-alabaster/80">in 30 Seconds</span>
          </h1>
          <p className="text-xl md:text-2xl text-alabaster/90 font-sans font-light max-w-3xl mx-auto mb-12 leading-relaxed">
            Make buyers say "I want it." Bright, airy, and irresistibly clickable. Turn empty rooms into offers with AI.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 px-8 py-4 bg-olive hover:bg-charcoal text-alabaster transition-all duration-500 text-lg font-medium rounded-full shadow-lift hover:-translate-y-0.5"
            >
              Get 3 Free Staged Photos
              <ArrowRight className="w-5 h-5" />
            </button>
            <Link
              to="/virtual-staging/before-after"
              className="inline-flex items-center px-8 py-4 border border-alabaster/40 hover:border-alabaster/70 text-white hover:text-white transition-all duration-500 text-lg font-light rounded-full backdrop-blur-sm"
            >
              See Before/After
            </Link>
          </div>
          <div className="text-sm text-alabaster/70 font-mono uppercase tracking-wider">
            No credit card • 30-second setup
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-px h-16 bg-alabaster/30">
            <div className="w-px h-8 bg-alabaster/60" />
          </div>
        </div>
      </section>

      {/* Trust Signals - Alabaster Section */}
      <section className="relative py-16 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-sand">Cancel Anytime</div>
              <div className="text-charcoal/80 font-light">No contracts. No hassle.</div>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-sand">Money-Back Guarantee</div>
              <div className="text-charcoal/80 font-light">30-day guarantee. Love it or get a full refund.</div>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-sand">Instant Results</div>
              <div className="text-charcoal/80 font-light">Staged photos ready in 30 seconds.</div>
            </div>
          </div>
        </div>
      </section>

      <LandingTransformationProof
        content={transformationProofContent}
        onGetStarted={onGetStarted}
      />

      {/* Interactive Before & After Comparison */}
      <section className="relative py-24 md:py-32 px-6 bg-charcoal overflow-hidden">
        {/* Accent glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[60vh] bg-olive/10 blur-3xl rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              See It In Action
            </div>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-serif font-light tracking-tight mb-6 leading-tight text-white">
              Drag to Reveal
              <br />
              <span className="text-alabaster/70">the Transformation</span>
            </h2>
            <p className="text-xl md:text-2xl text-alabaster/80 font-light max-w-2xl mx-auto leading-relaxed">
              From vacant to magazine-ready in 30 seconds. Same room, completely different feeling.
            </p>
          </div>

          {/* Active example context label */}
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-alabaster/80 transition-opacity duration-150">
              {COMPARISON_EXAMPLES[activeComparison].label}
              <span className="text-alabaster/40">&middot;</span>
              {COMPARISON_EXAMPLES[activeComparison].style}
            </span>
          </div>

          {/* Slider with fade transition on switch */}
          <div className={`max-w-5xl mx-auto transition-opacity duration-150 ${isSliderTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <ComparisonSlider
              key={activeComparison}
              beforeSrc={COMPARISON_EXAMPLES[activeComparison].before}
              afterSrc={COMPARISON_EXAMPLES[activeComparison].after}
              beforeAlt={`Empty ${COMPARISON_EXAMPLES[activeComparison].label.toLowerCase()}`}
              afterAlt={`${COMPARISON_EXAMPLES[activeComparison].label} after ${COMPARISON_EXAMPLES[activeComparison].style.toLowerCase()} staging`}
              beforeLabel="Before"
              afterLabel="After"
              aspectRatio="aspect-[16/9]"
              enableAutoDemo={true}
            />
          </div>

          {/* Example Selector */}
          <div className="mt-8 px-4 flex justify-center gap-x-3 gap-y-2 flex-wrap max-w-5xl mx-auto">
            {COMPARISON_EXAMPLES.map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleChange(index)}
                className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal ${
                  index === activeComparison
                    ? 'border-olive ring-2 ring-olive ring-offset-2 ring-offset-charcoal shadow-soft-lg'
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <div className="w-28 md:w-36 aspect-[16/9] overflow-hidden">
                  <img
                    src={example.after}
                    alt={`${example.label} ${example.style}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2">
                  <div className="text-[11px] font-medium text-white leading-tight">{example.label}</div>
                  <div className="text-[10px] text-alabaster/60">{example.style}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/virtual-staging/before-after"
              className="inline-flex items-center gap-2 text-alabaster/70 hover:text-alabaster border-b border-alabaster/30 hover:border-alabaster/60 transition-all duration-300 text-lg font-light"
            >
              Explore all before &amp; after examples
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section id="features" className="relative">
        <LandingListingProof
          content={listingProofContent}
          onGetStarted={onGetStarted}
        />
        <LandingStyleBreadthProof content={styleBreadthContent} />
        <LandingWorkflowProof
          content={workflowProofContent}
          onGetStarted={onGetStarted}
        />
      </section>

      {/* Styles Showcase - Alabaster Gallery */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Style Library
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              21 Virtual Staging Styles for Real Estate Listings
            </h2>
            <p className="text-xl text-sand font-light max-w-2xl mx-auto">
              Match the same room to the buyer you want to move, not just the furniture you happened
              to pick first.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {STYLE_SHOWCASE.map((item) => (
              <Link
                key={item.style}
                to={`/virtual-staging/styles/${item.style}`}
                className="group relative aspect-[3/4] overflow-hidden rounded-4xl bg-stone/30 hover:shadow-soft-lg transition-all duration-500"
              >
                <img
                  src={item.image}
                  alt={`${item.style} style`}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-end p-4">
                  <div className="text-alabaster group-hover:text-white font-medium text-sm capitalize transition-colors">
                    {item.style}
                  </div>
                </div>
                <div className="absolute inset-0 border border-stone/50 rounded-4xl" />
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/virtual-staging/styles"
              className="inline-flex items-center gap-2 text-sand hover:text-terracotta border-b border-sand/30 hover:border-terracotta/50 transition-all duration-300 text-lg font-light"
            >
              Explore All 21 Styles
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Real Estate Outcomes */}
      <section className="relative py-32 px-6 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Commercial Intent
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-alabaster">
              AI Virtual Staging for Real Estate Listings
            </h2>
            <p className="text-xl text-alabaster/70 font-light leading-relaxed">
              The homepage should answer the high-intent questions directly: can the tool create
              convincing before-and-after proof, fit real listing workflows, and adapt the room to
              different buyer audiences without a reshoot?
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {LISTING_OUTCOMES.map((item) => (
              <article
                key={item.title}
                className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 shadow-soft-xl"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                </div>
                <div className="space-y-5 p-8">
                  <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                    {item.eyebrow}
                  </div>
                  <h3 className="text-3xl font-serif font-light tracking-tight text-alabaster">
                    {item.title}
                  </h3>
                  <p className="text-base font-light leading-relaxed text-alabaster/70">
                    {item.description}
                  </p>
                  <div className="grid gap-3">
                    {item.bullets.map((bullet) => (
                      <div key={bullet} className="flex items-start gap-3 text-sm font-light text-alabaster/75">
                        <span className="mt-1 h-2 w-2 rounded-full bg-terracotta" />
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={item.to}
                    className="inline-flex items-center gap-2 border-b border-alabaster/20 pb-1 text-base font-light text-alabaster/75 transition-colors duration-300 hover:border-alabaster/60 hover:text-alabaster"
                  >
                    {item.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Alabaster with Cards */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Process
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              How AI Virtual Staging Works for Real Estate Agents
            </h2>
            <p className="text-xl text-sand font-light max-w-2xl mx-auto">
              The workflow needs to be operationally obvious: upload the listing photo, choose the
              style direction, then download a marketing asset the team can actually use.
            </p>
          </div>

          <div className="space-y-24">
            {HOME_WORKFLOW_STEPS.map((step, index) => (
              <div
                key={index}
                className={`grid md:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-4xl shadow-soft-xl bg-white">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    <div className="absolute inset-0 border border-stone rounded-4xl" />
                  </div>
                </div>
                <div className={`space-y-6 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div className="text-6xl md:text-8xl font-serif font-light text-olive/30">
                    {step.number}
                  </div>
                  <h3 className="text-3xl md:text-5xl font-serif font-light tracking-tight text-charcoal">
                    {step.title}
                  </h3>
                  <p className="text-xl text-sand font-light leading-relaxed">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {step.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-stone bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] text-sand shadow-soft"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Snapshots */}
      <section className="relative py-32 px-6 bg-white border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Workflow Snapshots
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              How Real Estate Teams Use AI Virtual Staging
            </h2>
            <p className="mx-auto max-w-3xl text-xl font-light leading-relaxed text-sand">
              These are the kinds of listing contexts the product is built for: seller presentations,
              spec-home media packages, and brokerage pipelines that need consistent output.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {AGENT_WORKFLOWS.map((workflow) => (
              <article
                key={workflow.role}
                className="space-y-6 rounded-4xl border border-stone bg-alabaster p-8 shadow-soft transition-shadow duration-500 hover:shadow-soft-lg"
              >
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] text-sand shadow-soft">
                    {workflow.role}
                  </span>
                  <span className="rounded-full bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] text-sand shadow-soft">
                    {workflow.market}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                    Listing context
                  </div>
                  <h3 className="mt-4 text-3xl font-serif font-light tracking-tight text-charcoal">
                    {workflow.listing}
                  </h3>
                  <p className="mt-5 text-base font-light leading-relaxed text-charcoal/75">
                    {workflow.benefit}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Alabaster with White Cards */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Pricing
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              Virtual Staging Pricing vs Traditional Staging
            </h2>
            <p className="text-xl text-sand font-light mb-8">
              Compare the operational model, then pick the image volume that matches how your listing
              pipeline actually works.
            </p>

            <div className="inline-flex items-center bg-white rounded-full p-1 border border-stone shadow-soft">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedPlan === 'monthly'
                    ? 'bg-olive text-alabaster'
                    : 'text-sand hover:text-charcoal'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedPlan('annual')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedPlan === 'annual'
                    ? 'bg-olive text-alabaster'
                    : 'text-sand hover:text-charcoal'
                }`}
              >
                Annual <span className="text-xs ml-1 text-terracotta">(Save 30%)</span>
              </button>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 mb-12">
            <div className="rounded-[2rem] border border-stone bg-white p-8 shadow-soft">
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                Traditional staging
              </div>
              <h3 className="mt-4 text-3xl font-serif font-light tracking-tight text-charcoal">
                Physical furniture, scheduling, and one installed look.
              </h3>
              <div className="mt-6 grid gap-3">
                {[
                  'Requires an on-site install and pickup',
                  'Harder to iterate across multiple buyer styles',
                  'Useful for in-person showings, but slower to change',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm font-light text-charcoal/75">
                    <span className="mt-1 h-2 w-2 rounded-full bg-terracotta" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[2rem] border border-olive/20 bg-charcoal p-8 text-alabaster shadow-soft-lg">
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                VirtualStagePro
              </div>
              <h3 className="mt-4 text-3xl font-serif font-light tracking-tight">
                Subscription-style pricing with fast restaging and MLS-ready delivery.
              </h3>
              <div className="mt-6 grid gap-3">
                {[
                  'Upload and stage in about 30 seconds',
                  'Restage the same room in multiple buyer-fit directions',
                  'Download HD or 4K files for listing media',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm font-light text-alabaster/75">
                    <span className="mt-1 h-2 w-2 rounded-full bg-olive" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {LANDING_PRICING_PLANS.map((plan) => {
              const price = selectedPlan === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
              const imageCount = selectedPlan === 'monthly' ? plan.monthlyImages : plan.annualImages;
              const perImage = (price / imageCount).toFixed(2);

              return (
              <div
                key={plan.name}
                className={`p-8 rounded-4xl border transition-all duration-500 ${
                  plan.highlight
                    ? 'bg-white border-olive shadow-soft-lg scale-105'
                    : 'bg-white border-stone shadow-soft hover:shadow-soft-lg'
                }`}
              >
                {plan.highlight && (
                  <div className="text-xs font-mono uppercase tracking-widest text-terracotta mb-4">Most Popular</div>
                )}
                <div className="mb-6">
                  <h3 className="text-2xl font-serif font-light mb-2 text-charcoal">{plan.name}</h3>
                  <p className="text-sand font-light text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline">
                    <span className="text-5xl font-serif font-light text-charcoal">${price}</span>
                    <span className="text-sand font-light ml-2">
                      /{selectedPlan === 'monthly' ? 'month' : 'month billed annually'}
                    </span>
                  </div>
                  <div className="mt-3 rounded-full bg-alabaster px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] text-sand">
                    About ${perImage} per staged image
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start text-charcoal/80 font-light">
                      <Check className="w-5 h-5 text-olive mr-3 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onGetStarted}
                  className={`w-full py-4 rounded-full transition-all duration-500 font-medium ${
                    plan.highlight
                      ? 'bg-olive text-alabaster hover:bg-charcoal shadow-soft hover:-translate-y-0.5'
                      : 'bg-transparent text-charcoal border border-stone hover:border-olive hover:text-olive'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
              );
            })}
          </div>

          <div className="text-center space-y-4">
            <p className="text-sand font-light">
              Virtual staging pricing starts at $19 per month, or about $0.32 per staged image on the
              Starter plan.
            </p>
            <Link
              to="/virtual-staging/pricing"
              className="inline-flex items-center gap-2 text-charcoal border-b border-charcoal/20 hover:border-olive hover:text-olive transition-all duration-300 text-lg font-light"
            >
              Compare all pricing details
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ - Warm Dark (Charcoal) */}
      <section className="relative py-32 px-6 bg-charcoal">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              FAQ
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-alabaster">
              AI Virtual Staging Questions Real Estate Agents Ask
            </h2>
            <p className="mx-auto max-w-3xl text-xl font-light leading-relaxed text-alabaster/70">
              Visible answers beat hidden accordions when buyers, sellers, search engines, and AI
              agents are all trying to understand the workflow quickly.
            </p>
            <div className="mt-8">
              <Link
                to="/virtual-staging/faq"
                className="inline-flex items-center gap-2 text-alabaster/80 border-b border-alabaster/30 hover:border-alabaster/60 hover:text-alabaster transition-all duration-300 text-lg font-light"
              >
                Read the full AI virtual staging FAQ
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {homepageFaqItems.map((faq) => (
              <article
                key={faq.id}
                className="rounded-[2rem] border border-white/10 bg-white/5 p-8 shadow-soft"
              >
                <h3 className="text-2xl font-serif font-light tracking-tight text-white">
                  {faq.question}
                </h3>
                <p className="mt-5 text-base font-medium leading-relaxed text-alabaster">
                  {faq.shortAnswer}
                </p>
                <p className="mt-4 text-sm font-light leading-relaxed text-alabaster/70">
                  {faq.answer}
                </p>
                {faq.sources && faq.sources.length > 0 && (
                  <CitationList sources={faq.sources} tone="dark" className="mt-8" />
                )}
              </article>
            ))}
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <Link
              to="/virtual-staging/faq"
              className="rounded-[2rem] border border-white/10 bg-white/5 p-8 transition-all duration-500 hover:border-white/20 hover:bg-white/[0.08]"
            >
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                FAQ Hub
              </div>
              <h3 className="mt-5 text-3xl font-serif font-light tracking-tight text-white">
                Need the full 12-question guide?
              </h3>
              <p className="mt-4 text-base font-light leading-relaxed text-alabaster/70">
                Go deeper on virtual staging pricing, occupied rooms, exterior photos, MLS use, image
                quality, and brokerage-scale workflows.
              </p>
            </Link>
            <Link
              to="/virtual-staging/interior-ai-alternative"
              className="rounded-[2rem] border border-olive/20 bg-olive/10 p-8 transition-all duration-500 hover:border-olive/40 hover:bg-olive/15"
            >
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                Comparison
              </div>
              <h3 className="mt-5 text-3xl font-serif font-light tracking-tight text-white">
                Looking for an Interior AI alternative built for real estate?
              </h3>
              <p className="mt-4 text-base font-light leading-relaxed text-alabaster/70">
                Compare real-estate focus, listing workflow clarity, MLS guidance, and buyer-facing
                proof without guessing from generic AI design messaging.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA - Immersive with Warm Overlay */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/styles-page/luxury_staging_style.webp"
            alt="Luxury staged room"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/60 to-olive/40" />
        </div>

        {/* Terracotta glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[60vh] bg-terracotta/10 blur-3xl rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-8 text-white">
            Start AI Virtual Staging
            <br />
            <span className="text-alabaster/70">for Your Next Listing</span>
          </h2>
          <p className="text-xl md:text-2xl text-alabaster/80 font-light max-w-2xl mx-auto mb-12 leading-relaxed">
            Create before-and-after proof, choose from 21 buyer-fit styles, and download MLS-ready
            listing images in about 30 seconds.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 px-12 py-4 bg-olive hover:bg-terracotta text-alabaster transition-all duration-500 text-lg font-medium rounded-full shadow-lift hover:-translate-y-0.5"
            >
              Get 3 Free Staged Photos
              <ArrowRight className="w-5 h-5" />
            </button>
            <Link
              to="/virtual-staging/pricing"
              className="inline-flex items-center px-12 py-4 border border-alabaster/35 hover:border-alabaster/70 text-white transition-all duration-500 text-lg font-light rounded-full backdrop-blur-sm"
            >
              View Virtual Staging Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
